import { takeUntil } from 'rxjs/operators';

import { requestStream } from '@swf/core/api/requestStream';
import { Brand } from '@swf/core/enums';
import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';
import type { RewardsStateModel } from '@swf/core/types/Rewards';
import { defaultCvlBar, defaultCvlConfig } from '@swf/core/types/Rewards';
import { logout } from '@swf/core/utils/actions';

interface RewardsModel extends ModelConfig<RewardsState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export interface RewardsState {
  data?: RewardsStateModel;
  subscriptions: { [path: string]: string };
  oldPoints: number;
}

export enum RewardsBrand {
  FoxBet = 'FOX Bet',
  PokerStars = 'PokerStars'
}

const defaultState = {
  subscriptions: {},
  oldPoints: 0
};

const reducers = {
  set: (state: RewardsState, payload: RewardsState) => ({ ...state, ...payload }),
  'session/clear': (): RewardsState => defaultState
};

const effects = {
  async fetchRewards(payload: undefined, { dispatch, rootState, actions }: EffectParams) {
    const options = select.cms.getRequestOptions(rootState);
    const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;

    requestStream<RewardsState>({
      ...options,
      service: '/api/v1-preview/cvlbar'
    })
      .pipe(takeUntil(onLogout))
      .subscribe((update) => {
        dispatch.rewards.set(update);
      });
  }
};

const selectors = (slice: Slicer<RewardsState>, createSelector: SelectorCreator) => ({
  get() {
    return slice((state) => state || {});
  },
  getCvlBarData() {
    return slice((state) => state.data?.cvlBar || defaultCvlBar);
  },
  getCvlConfigData() {
    return slice((state) => state.data?.cvlConfig || defaultCvlConfig);
  },
  getRewardsBrand() {
    return createSelector(
      (rootState: RootState) => select.cms.getBrandId(rootState),
      (brand) => {
        switch (brand) {
          case Brand.FoxBet:
            return RewardsBrand.FoxBet;
          default:
            return RewardsBrand.PokerStars;
        }
      }
    );
  }
});

const rewards: RewardsModel = {
  state: defaultState,
  reducers,
  effects,
  selectors
};

export default rewards;
